import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Archive, Trash2, Loader2, AlertTriangle } from "lucide-react";
interface EmployeeArchiveActionsProps {
  employeeId: string;
  employeeName: string;
  isArchived?: boolean;
  onArchiveStatusChange?: () => void;
}
export function EmployeeArchiveActions({
  employeeId,
  employeeName,
  isArchived = false,
  onArchiveStatusChange
}: EmployeeArchiveActionsProps) {
  const {
    user,
    userRole
  } = useAuth();
  const navigate = useNavigate();
  const [archiving, setArchiving] = useState(false);
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showDeleteConfirmDialog, setShowDeleteConfirmDialog] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const [deleting, setDeleting] = useState(false);
  const canManage = userRole === "admin" || userRole === "hr_manager";
  if (!canManage) return null;
  const handleArchive = async () => {
    setArchiving(true);
    try {
      const {
        error
      } = await supabase.from("employees").update({
        archived_at: isArchived ? null : new Date().toISOString(),
        archived_by: isArchived ? null : user?.id
      }).eq("id", employeeId);
      if (error) throw error;
      toast.success(isArchived ? "Mitarbeiter wiederhergestellt" : "Mitarbeiter archiviert");
      setShowArchiveDialog(false);
      if (onArchiveStatusChange) {
        onArchiveStatusChange();
      } else if (!isArchived) {
        navigate("/employees");
      }
    } catch (error) {
      console.error("Error archiving employee:", error);
      toast.error("Fehler beim Archivieren");
    } finally {
      setArchiving(false);
    }
  };
  const handleDelete = async () => {
    if (deleteConfirmation !== employeeName) {
      toast.error("Name stimmt nicht überein");
      return;
    }
    setDeleting(true);
    try {
      // Delete related data first
      await Promise.all([supabase.from("employee_clothing").delete().eq("employee_id", employeeId), supabase.from("employee_tools").delete().eq("employee_id", employeeId), supabase.from("employee_documents").delete().eq("employee_id", employeeId), supabase.from("employee_external_credentials").delete().eq("employee_id", employeeId), supabase.from("salary_history").delete().eq("employee_id", employeeId), supabase.from("bonus_history").delete().eq("employee_id", employeeId), supabase.from("benefits_history").delete().eq("employee_id", employeeId), supabase.from("vehicle_costs").delete().eq("employee_id", employeeId), supabase.from("employee_notes").delete().eq("employee_id", employeeId)]);

      // Delete evaluations and their related data
      const {
        data: evaluations
      } = await supabase.from("evaluations").select("id").eq("employee_id", employeeId);
      if (evaluations && evaluations.length > 0) {
        const evalIds = evaluations.map(e => e.id);

        // Get criteria ratings
        const {
          data: ratings
        } = await supabase.from("criteria_ratings").select("id").in("evaluation_id", evalIds);
        if (ratings && ratings.length > 0) {
          const ratingIds = ratings.map(r => r.id);
          await supabase.from("rating_comments").delete().in("rating_id", ratingIds);
        }
        await supabase.from("criteria_ratings").delete().in("evaluation_id", evalIds);
        await supabase.from("evaluation_history").delete().in("evaluation_id", evalIds);
        await supabase.from("evaluations").delete().eq("employee_id", employeeId);
      }

      // Finally delete the employee
      const {
        error
      } = await supabase.from("employees").delete().eq("id", employeeId);
      if (error) throw error;
      toast.success("Mitarbeiter und alle zugehörigen Daten wurden gelöscht");
      navigate("/employees");
    } catch (error) {
      console.error("Error deleting employee:", error);
      toast.error("Fehler beim Löschen des Mitarbeiters");
    } finally {
      setDeleting(false);
      setShowDeleteConfirmDialog(false);
      setShowDeleteDialog(false);
    }
  };
  return <>
      <Card className="border-destructive/20 bg-destructive/5">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            Mitarbeiterverwaltung
          </CardTitle>
          <CardDescription>Archivieren oder Löschen des Mitarbeiters</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col sm:flex-row gap-3">
          <Button variant="outline" onClick={() => setShowArchiveDialog(true)} className="flex-1">
            <Archive className="h-4 w-4 mr-2" />
            {isArchived ? "Wiederherstellen" : "Archivieren"}
          </Button>
          <Button variant="destructive" onClick={() => setShowDeleteDialog(true)} className="flex-1">
            <Trash2 className="h-4 w-4 mr-2" />
            Endgültig Löschen
          </Button>
        </CardContent>
      </Card>

      {/* Archive Dialog */}
      <AlertDialog open={showArchiveDialog} onOpenChange={setShowArchiveDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {isArchived ? "Mitarbeiter wiederherstellen?" : "Mitarbeiter archivieren?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {isArchived ? `Möchten Sie "${employeeName}" aus dem Archiv wiederherstellen? Der Mitarbeiter wird wieder in der aktiven Liste angezeigt.` : `Möchten Sie "${employeeName}" archivieren? Der Mitarbeiter wird aus der aktiven Liste entfernt, bleibt aber im Archiv erhalten.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={handleArchive} disabled={archiving}>
              {archiving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isArchived ? "Wiederherstellen" : "Archivieren"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* First Delete Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-destructive flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Mitarbeiter endgültig löschen?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>
                <strong>Achtung:</strong> Diese Aktion kann nicht rückgängig gemacht werden!
              </p>
              <p>
                Folgende Daten werden unwiderruflich gelöscht:
              </p>
              <ul className="list-disc list-inside text-sm space-y-1 mt-2">
                <li>Alle Stammdaten des Mitarbeiters</li>
                <li>Gehalts- und Bonushistorie</li>
                <li>Benefits und KFZ-Kosten</li>
                <li>Bewertungen und Bewertungshistorie</li>
                <li>Dokumente und Zugangsdaten</li>
                <li>Arbeitskleidung und Werkzeuge</li>
              </ul>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={() => {
            setShowDeleteDialog(false);
            setShowDeleteConfirmDialog(true);
          }} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Weiter zur Bestätigung
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Second Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteConfirmDialog} onOpenChange={open => {
      setShowDeleteConfirmDialog(open);
      if (!open) setDeleteConfirmation("");
    }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-destructive flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Endgültige Bestätigung
            </AlertDialogTitle>
            <AlertDialogDescription>
              Zur Bestätigung geben Sie bitte den vollständigen Namen des Mitarbeiters ein:
              <br />
              <strong className="text-foreground">{employeeName}</strong>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <Label htmlFor="confirm-name">Mitarbeitername zur Bestätigung</Label>
            <Input id="confirm-name" value={deleteConfirmation} onChange={e => setDeleteConfirmation(e.target.value)} placeholder={employeeName} className="mt-2" />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteConfirmation("")}>
              Abbrechen
            </AlertDialogCancel>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting || deleteConfirmation !== employeeName}>
              {deleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Endgültig Löschen
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>;
}